import React, { useState, useEffect } from 'react';
import './contactus.css';
import { FaPhone, FaMapMarkerAlt, FaClock, FaEnvelope, FaFacebook, FaInstagram } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../AppointmentComp/firebase'; // Adjust path as needed

function ContactUs() {
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState('contact');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState('');

  // Handle form input changes
  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSubmitSuccess(false);

    try {
      // Add document to Firestore
      await addDoc(collection(db, 'contactus'), {
        ...formData,
        createdAt: serverTimestamp(),
        status: 'unread'
      });
      
      setSubmitSuccess(true);
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: ''
      });
      
      // Hide success message after 5 seconds
      setTimeout(() => setSubmitSuccess(false), 5000);
    } catch (err) {
      console.error("Error submitting contact form:", err);
      setError('Failed to send message. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  // Animation variants (keep your existing animation code)
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        when: "beforeChildren"
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  useEffect(() => {
    // Simulate map loading
    const timer = setTimeout(() => {
      setIsMapLoaded(true);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);


  return (
    <motion.div 
      className="contact-us-container"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <motion.h1 variants={itemVariants}>Contact Us</motion.h1>
      
      <div className="tab-buttons">
        <button 
          className={activeTab === 'contact' ? 'active' : ''}
          onClick={() => setActiveTab('contact')}
        >
          Contact Info
        </button>
      </div>

      {activeTab === 'contact' && (
        <motion.div 
          className="clinic-info"
          variants={containerVariants}
        >
          <motion.h2 variants={itemVariants}>
            Eye Care Clinic & Optical
          </motion.h2>
          
          <motion.div 
            className="contact-cards"
            variants={containerVariants}
          >
            <motion.div className="contact-card" variants={itemVariants}>
              <div className="icon-container">
                <FaMapMarkerAlt className="contact-icon" />
              </div>
              <div>
                <h3>Address</h3>
                <p>932, Maradana Road, Colombo 08</p>
                <button 
                  className="map-button"
                  onClick={() => {
                    setActiveTab('map');
                    window.scrollTo({
                      top: document.querySelector('.map-container').offsetTop - 20,
                      behavior: 'smooth'
                    });
                  }}
                >
                  View on Map
                </button>
              </div>
            </motion.div>
            
            <motion.div className="contact-card" variants={itemVariants}>
              <div className="icon-container">
                <FaPhone className="contact-icon" />
              </div>
              <div>
                <h3>Phone</h3>
                <a href="tel:+94112682277">011 268 2277</a>
                <a href="tel:+94761360944">076 136 0944</a>
              </div>
            </motion.div>

            <motion.div className="contact-card" variants={itemVariants}>
              <div className="icon-container">
                <FaEnvelope className="contact-icon" />
              </div>
              <div>
                <h3>Email</h3>
                <a href="mailto:eyecareoptical2024@gmail.com">eyecareoptical2024@gmail.com</a>
              </div>
            </motion.div>
          </motion.div>

        </motion.div>
      )}



      <div className={`map-container ${isMapLoaded ? 'loaded' : ''}`}>
        {!isMapLoaded && <div className="map-placeholder">Loading map...</div>}
        <iframe 
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3960.7513034374824!2d79.86938077448274!3d6.920306318426057!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ae259744a1d7131%3A0x614fbd9988cf7417!2sEye%20Care%20Institute!5e0!3m2!1sen!2slk!4v1752831435644!5m2!1sen!2slk" 
          width="100%" 
          height="450" 
          style={{border:0}} 
          allowFullScreen="" 
          loading="lazy" 
          referrerPolicy="no-referrer-when-downgrade"
          title="Clinic Location Map"
          onLoad={() => setIsMapLoaded(true)}
        ></iframe>
      </div>

<motion.div 
        className="contact-form-container"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <h3>Send us a message</h3>
        {submitSuccess && (
          <div className="success-message">
            Thank you! Your message has been sent successfully.
          </div>
        )}
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
        <form className="contact-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Name</label>
            <input 
              type="text" 
              id="name" 
              required 
              value={formData.name}
              onChange={handleInputChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input 
              type="email" 
              id="email" 
              required 
              value={formData.email}
              onChange={handleInputChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="phone">Phone</label>
            <input 
              type="tel" 
              id="phone" 
              value={formData.phone}
              onChange={handleInputChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="message">Message</label>
            <textarea 
              id="message" 
              rows="5" 
              required 
              value={formData.message}
              onChange={handleInputChange}
            ></textarea>
          </div>
          <button 
            type="submit" 
            className="submit-button"
            disabled={submitting}
          >
            {submitting ? 'Sending...' : 'Send Message'}
          </button>
        </form>
      </motion.div>
    </motion.div>
  );
}

export default ContactUs;